// ============================================================================
// GIXCORE PERFORMANCE SCORING — "the etalon" (JS port for the Vue 2 builder).
// Mirrors gixcore-nuxt/shared/utils/scoring.ts so the builder and the SEO app
// score parts identically. Adapted to the builder's part shape:
//   item.specs, item.compat (singular), item.amzn_price, item.model, item.type
//
// Raw -> capacity -> normalized(0-100) against a per-type ceiling.
// Benchmarks drive CPU when broadly present + clean; formula is the fallback.
// ============================================================================

// ---- defensive parsing ----
function pick (bagList, keys) {
  for (var i = 0; i < bagList.length; i++) {
    var bag = bagList[i]
    if (!bag) continue
    var lower = {}
    Object.keys(bag).forEach(function (k) { lower[k.toLowerCase()] = bag[k] })
    for (var j = 0; j < keys.length; j++) {
      var v = lower[keys[j].toLowerCase()]
      if (v == null) continue
      var s = String(v).trim()
      if (s === '' || s.toUpperCase() === 'N/A' || s === '-' || s === '0') continue
      return s
    }
  }
  return undefined
}

function num (s) {
  if (s == null) return null
  var m = String(s).replace(/,/g, '').match(/-?\d+(\.\d+)?/)
  if (!m) return null
  var n = parseFloat(m[0])
  return isNaN(n) ? null : n
}

// Strict: only when the WHOLE value is a bare number (rejects free-text benchmarks).
function cleanNum (s) {
  if (s == null) return null
  var t = String(s).trim().replace(/,/g, '')
  return /^\d+(\.\d+)?$/.test(t) ? parseFloat(t) : null
}

function toMHz (s) {
  var n = num(s)
  if (n == null) return null
  if (/ghz/i.test(s || '')) return n * 1000
  if (n > 0 && n < 100) return n * 1000
  return n
}

function toGB (s) {
  var n = num(s)
  if (n == null) return null
  if (/tb/i.test(s || '')) return n * 1024
  if (/mb/i.test(s || '')) return n / 1024
  return n
}

function bags (item) {
  return [item && item.specs, item && item.compat]
}

function priceOf (item) {
  var raw = pick([item], ['amzn_price', 'price']) || pick(bags(item), ['price', 'amzn_price'])
  var n = num(raw)
  return n && n > 0 ? n : null
}

// ---- architecture coefficients ----
var GPU_ARCH = [
  { test: /\brtx\s*50\d{2}/i, coef: 1.7 },
  { test: /\brtx\s*40\d{2}/i, coef: 1.45 },
  { test: /\brtx\s*30\d{2}/i, coef: 1.15 },
  { test: /\brtx\s*20\d{2}/i, coef: 1.0 },
  { test: /\bgtx\s*16\d{2}/i, coef: 0.85 },
  { test: /\bgtx\s*10\d{2}/i, coef: 0.7 },
  { test: /\brx\s*9\d{3}/i, coef: 1.5 },
  { test: /\brx\s*7\d{3}/i, coef: 1.35 },
  { test: /\brx\s*6\d{3}/i, coef: 1.1 },
  { test: /\barc\s*[ab]\d{3}/i, coef: 1.05 }
]
function gpuArchCoef (model) {
  if (!model) return 1.0
  for (var i = 0; i < GPU_ARCH.length; i++) {
    if (GPU_ARCH[i].test.test(model)) return GPU_ARCH[i].coef
  }
  return 1.0
}

var RAM_GEN = { DDR3: 0.7, DDR4: 1.0, DDR5: 1.35 }

var STORAGE_CLASS = [
  { test: /nvme|pci.?e|gen\s?[45]/i, base: 85 },
  { test: /ssd|sata.*ssd/i, base: 55 },
  { test: /hdd|7200|5400|rpm/i, base: 15 }
]

// ---- per-axis raw capacity ----
// Returns { raw, source, axis } or null. opts.ignoreBenchmark forces the CPU formula.
export function rawCapacity (item, opts) {
  if (!item || !item.type || !item.specs || !item.compat) return null
  var type = item.type === 'graphics' ? 'gpu' : item.type
  var b = bags(item)

  if (type === 'cpu') {
    var bench = (opts && opts.ignoreBenchmark) ? null : cleanNum(pick(b, ['CPU_Z_Benchmark', 'benchmark']))
    if (bench && bench > 0) return { raw: bench, source: 'benchmark', axis: 'compute' }
    var clock = toMHz(pick(b, ['Core_Frequency', 'Turbo_Frequency']))
    var cores = num(pick(b, ['Number_of_Cores']))
    var ht = pick(b, ['Hyper_Threading'])
    if (!clock || !cores) return null
    var cpuPerf = (clock / 1000) * cores
    if (ht && ht !== 'No') cpuPerf *= 1.3
    return { raw: cpuPerf, source: 'estimated', axis: 'compute' }
  }

  if (type === 'gpu') {
    var coreMHz = toMHz(pick(b, ['GPU_clock_speed']))
    var vram = toGB(pick(b, ['graphics_ram_capacity']))
    // Bandwidth from two unreliable catalog sources — take the larger plausible
    // one (memory_clock_speed mixes real vs effective clock; rated transfer is
    // sometimes garbage like "1 GB/s").
    var bandwidth = 0
    var memMHz = toMHz(pick(b, ['memory_clock_speed']))
    var bus = num(pick(b, ['memory_bus_width']))
    if (memMHz && bus) bandwidth = (memMHz * bus) / 8000
    var rated = num(pick(b, ['memory_transfer_speed']))
    if (rated && rated > 20 && rated > bandwidth) bandwidth = rated
    if (!coreMHz || bandwidth <= 0) return null
    var arch = gpuArchCoef(pick(b, ['graphics_processor']) || item.model)
    var vramFactor = vram ? 1 + Math.min(vram, 24) / 96 : 1
    var raw = coreMHz * bandwidth * arch * vramFactor
    return { raw: raw, source: arch === 1.0 ? 'estimated' : 'formula', axis: 'render' }
  }

  if (type === 'ram') {
    var speed = num(pick(b, ['RAM_Speed', 'ram_speed']))
    var gen = (pick(b, ['RAM_Type', 'ram_type']) || 'DDR4').toUpperCase()
    if (!speed) return null
    var genCoef = RAM_GEN[gen] != null ? RAM_GEN[gen] : 1.0
    return { raw: speed * genCoef, source: 'formula', axis: 'memory' }
  }

  if (type === 'storage') {
    var typeStr = [pick(b, ['Disk_Type', 'disk_type']), pick(b, ['hardware_interface', 'compat_interface']), item.model]
      .filter(Boolean).join(' ')
    var base = null
    for (var i = 0; i < STORAGE_CLASS.length; i++) {
      if (STORAGE_CLASS[i].test.test(typeStr)) { base = STORAGE_CLASS[i].base; break }
    }
    if (base == null) return null
    var sp = num(pick(b, ['data_transfer_rate', 'read_speed', 'speed']))
    var bump = sp ? Math.min(sp / 7000, 1) * 15 : 0
    return { raw: base + bump, source: 'formula', axis: 'responsiveness' }
  }

  return null
}

export function hasCapacity (type) {
  var t = type === 'graphics' ? 'gpu' : type
  return t === 'cpu' || t === 'gpu' || t === 'ram' || t === 'storage'
}

// ---- source consistency (benchmark spine only when broadly present) ----
export var BENCHMARK_MIN_COVERAGE = 0.5

export function benchmarkCoverage (parts) {
  var total = 0; var covered = 0
  for (var i = 0; i < parts.length; i++) {
    var p = parts[i]
    if (!p || (p.type !== 'cpu')) continue
    total++
    if (cleanNum(pick(bags(p), ['CPU_Z_Benchmark', 'benchmark'])) != null) covered++
  }
  return total ? covered / total : 0
}

export function shouldIgnoreBenchmark (parts) {
  if (!parts || !parts.length) return false
  var first = parts.find(function (p) { return p && p.type })
  if (!first || first.type !== 'cpu') return false
  return benchmarkCoverage(parts) < BENCHMARK_MIN_COVERAGE
}

// ---- normalization ----
// ROBUST band: top-K avg vs bottom-K avg (not single max/min), so a couple of
// HEDT outliers don't crush the whole mainstream into the red zone.
export var ROBUST_SAMPLE = 10
// GPUs have a long flagship tail, so a tighter top-5 band reads better than top-10.
var ROBUST_SAMPLE_BY_TYPE = { gpu: 5, graphics: 5 }

export function buildCeiling (parts, opts) {
  var raws = []
  var sampleType = null
  for (var i = 0; i < parts.length; i++) {
    if (!sampleType && parts[i] && parts[i].type) sampleType = parts[i].type
    var c = rawCapacity(parts[i], opts)
    if (c && c.raw > 0) raws.push(c.raw)
  }
  raws.sort(function (a, b) { return b - a })
  var sample = (sampleType && ROBUST_SAMPLE_BY_TYPE[sampleType]) || ROBUST_SAMPLE
  var k = Math.min(sample, Math.max(1, Math.floor(raws.length / 2)))
  function avg (arr) {
    if (!arr.length) return 0
    var s = 0
    for (var j = 0; j < arr.length; j++) s += arr[j]
    return s / arr.length
  }
  var capacityTop = raws.length ? avg(raws.slice(0, k)) : 0
  var capacityFloor = raws.length ? avg(raws.slice(raws.length - k)) : 0
  return { capacityTop: capacityTop, capacityFloor: capacityFloor }
}

export function normalizedScore (raw, ceiling) {
  var top = ceiling && ceiling.capacityTop
  if (!top || top <= 0) return 0
  var base = (ceiling && ceiling.capacityFloor) || 0
  if (top <= base) return Math.max(0, Math.min(100, (raw / top) * 100))
  return Math.max(0, Math.min(100, ((raw - base) / (top - base)) * 100))
}

export function scoreTier (score) {
  if (score == null) return null
  if (score < 34) return 'low'
  if (score < 67) return 'mid'
  return 'high'
}

// ---- value (performance per dollar) ----
// Mirrors scoring.ts: capacity/price ratio, normalized 0-100 against the best
// ratio in the category TODAY. Reads the scraped price purely to RANK value —
// the dollar figure itself is never surfaced when prices are hidden, so this is
// the "bang for the buck" signal that survives the Amazon price-display rules.
export function buildValueCeiling (parts, opts) {
  var ratioTop = 0
  for (var i = 0; i < parts.length; i++) {
    var c = rawCapacity(parts[i], opts)
    var price = priceOf(parts[i])
    if (c && price && price > 0) {
      var r = c.raw / price
      if (r > ratioTop) ratioTop = r
    }
  }
  return { ratioTop: ratioTop }
}

// 0-100 value index for a single part, or null when it has no perf axis / price.
export function valueIndex (item, valueCeiling, opts) {
  if (!valueCeiling || !(valueCeiling.ratioTop > 0)) return null
  var c = rawCapacity(item, opts)
  var price = priceOf(item)
  if (!c || !price || price <= 0) return null
  var ratio = c.raw / price
  return Math.max(0, Math.min(100, (ratio / valueCeiling.ratioTop) * 100))
}

export default {
  rawCapacity: rawCapacity,
  hasCapacity: hasCapacity,
  buildCeiling: buildCeiling,
  normalizedScore: normalizedScore,
  scoreTier: scoreTier,
  shouldIgnoreBenchmark: shouldIgnoreBenchmark,
  benchmarkCoverage: benchmarkCoverage,
  buildValueCeiling: buildValueCeiling,
  valueIndex: valueIndex,
  priceOf: priceOf
}
